import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Loader2, Check, X } from 'lucide-react'
import { useState } from 'react'

import {
  ProspectRecommendationStatus,
  RecommendedProspectRow,
  prospectsApi,
} from '@/services/api'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'

const STATUS_OPTIONS: ProspectRecommendationStatus[] = ['new', 'accepted', 'dismissed']

export default function RecommendedProspectsPage() {
  const queryClient = useQueryClient()
  const [status, setStatus] = useState<ProspectRecommendationStatus>('new')
  const [page, setPage] = useState(1)

  const { data, isLoading } = useQuery({
    queryKey: ['prospects', 'recommended', status, page],
    queryFn: () => prospectsApi.listRecommended({ status, page, per_page: 20 }),
  })

  const acceptMutation = useMutation({
    mutationFn: (recommendationId: string) => prospectsApi.acceptRecommended(recommendationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['prospects', 'recommended'] })
      queryClient.invalidateQueries({ queryKey: ['companies'] })
    },
  })

  const dismissMutation = useMutation({
    mutationFn: (recommendationId: string) => prospectsApi.dismissRecommended(recommendationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['prospects', 'recommended'] })
    },
  })

  const rows: RecommendedProspectRow[] = data?.items || []
  const total = data?.total || 0

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Recommended Prospects</h1>
          <p className="text-muted-foreground mt-1">
            Deterministic cross-org suggestions. Manual accept only.
          </p>
        </div>
        <div className="flex gap-2">
          {STATUS_OPTIONS.map((option) => (
            <Button
              key={option}
              variant={status === option ? 'default' : 'outline'}
              size="sm"
              onClick={() => {
                setStatus(option)
                setPage(1)
              }}
            >
              {option}
            </Button>
          ))}
        </div>
      </div>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Company</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Score</TableHead>
                <TableHead>Reasons</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="w-[180px]">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-10 text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin mx-auto mb-2" />
                    Loading recommendations...
                  </TableCell>
                </TableRow>
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-10 text-muted-foreground">
                    No recommendations in this queue state.
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell>
                      <div className="flex flex-col">
                        <span className="font-medium">{row.company.name || row.raw_company_id}</span>
                        {row.company.website ? (
                          <a
                            href={row.company.website}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-muted-foreground hover:underline"
                          >
                            {row.company.website}
                          </a>
                        ) : null}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm">
                        <div>{row.company.company_type || '-'}</div>
                        {row.company.company_type_tags.length > 0 ? (
                          <div className="text-xs text-muted-foreground">
                            {row.company.company_type_tags.slice(0, 2).join(', ')}
                          </div>
                        ) : null}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge>{row.fit_score}</Badge>
                    </TableCell>
                    <TableCell>
                      <div className="text-xs text-muted-foreground">
                        {row.fit_reasons.slice(0, 2).join(' • ') || '-'}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant={row.status === 'new' ? 'secondary' : 'outline'}>{row.status}</Badge>
                    </TableCell>
                    <TableCell>
                      {row.status === 'new' ? (
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            onClick={() => acceptMutation.mutate(row.id)}
                            disabled={acceptMutation.isPending || dismissMutation.isPending}
                          >
                            <Check className="h-4 w-4 mr-1" />
                            Accept
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => dismissMutation.mutate(row.id)}
                            disabled={acceptMutation.isPending || dismissMutation.isPending}
                          >
                            <X className="h-4 w-4 mr-1" />
                            Dismiss
                          </Button>
                        </div>
                      ) : (
                        <span className="text-xs text-muted-foreground">Reviewed</span>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>

          {total > 20 ? (
            <div className="flex items-center justify-between border-t p-3">
              <span className="text-sm text-muted-foreground">
                Page {page} of {Math.max(1, Math.ceil(total / 20))}
              </span>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => p + 1)}
                  disabled={page * 20 >= total}
                >
                  Next
                </Button>
              </div>
            </div>
          ) : null}
        </CardContent>
      </Card>
    </div>
  )
}
